// src/pages/ProfilePage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Card, Form, Input, InputNumber, DatePicker, Button, message, Typography, Space, Row, Col, Upload, Avatar, Select, Divider, Alert, Switch, Modal } from 'antd';
import { UserOutlined, MailOutlined, CalendarOutlined, CameraOutlined } from '@ant-design/icons';
import { useAuth } from '../App';
import { authAPI, doctorLinkAPI } from '../utils/api';
import { DoctorPublicIdCard } from '../components/DoctorPublicIdCard';
import { parseDoctorIdFromQrText } from '../utils/doctorQr';
import { format } from 'date-fns';
import dayjs, { Dayjs } from 'dayjs';
import { PhoneInput } from '../components/PhoneInput';
import { getPhoneDigitCount, normalizePhone, PHONE_REQUIRED_MESSAGE, PHONE_VALIDATION_MESSAGE } from '../utils/phoneUtils';
import { getCurrentWeekOfPregnancy } from '../utils/pregnancyUtils';
import { calculateDueDateFromWeek, calculateWeekFromDueDate } from '../utils/pregnancyDates';
import { joinFullName, splitFullName } from '../utils/fullName';
import { appendUploadToFormData, getFilePreviewUrl, getUploadRawFile } from '../utils/fileUrls';

const { Title } = Typography;

export const ProfilePage: React.FC = () => {
  const [form] = Form.useForm();
  const { user, login, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const pregnancySyncLockRef = useRef<'week' | 'due' | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [doctorDisplayLabel, setDoctorDisplayLabel] = useState<string>('Врач не назначен');
  const [doctorLinkLoading, setDoctorLinkLoading] = useState(false);
  const [doctorLinkBundle, setDoctorLinkBundle] = useState<any>(null);
  const [clinicsOptions, setClinicsOptions] = useState<{ label: string; value: number }[]>([]);
  const [doctorSearchClinic, setDoctorSearchClinic] = useState<number | undefined>(undefined);
  const [doctorSearchQ, setDoctorSearchQ] = useState('');
  const [doctorSearchResults, setDoctorSearchResults] = useState<any[]>([]);
  const [linkForm] = Form.useForm();
  const [doctorQrInfo, setDoctorQrInfo] = useState<{
    deep_link?: string;
    clinic_name?: string;
    clinic_city?: string;
  } | null>(null);
  const [qrPaste, setQrPaste] = useState('');
  const doctorPublicId = (user as any)?.doctor_public_id;

  const labelFromDoctorBundle = (bundle: any): string => {
    const status = bundle?.link?.status;
    if (status === 'pending') return 'Заявка на рассмотрении';
    if (status === 'active') return bundle?.doctor_full_name || 'Назначен';
    return 'Врач не назначен';
  };

  useEffect(() => {
    const loadDoctorLinkState = async () => {
      if (user?.role !== 'patient') {
        setDoctorLinkBundle(null);
        setDoctorDisplayLabel('');
        return;
      }
      try {
        setDoctorLinkLoading(true);
        const [stateRes, clinicsRes] = await Promise.all([
          doctorLinkAPI.getDoctorLinkState(),
          doctorLinkAPI.getClinics().catch(() => ({ data: [] as any[] })),
        ]);
        const bundle = (stateRes.data as any)?.bundle || null;
        setDoctorLinkBundle(bundle);
        setDoctorDisplayLabel(labelFromDoctorBundle(bundle));
        const raw = (clinicsRes.data as any[]) || [];
        setClinicsOptions(
          raw.map((c: any) => ({ value: c.id, label: `${c.name}${c.city ? ` (${c.city})` : ''}` }))
        );
      } catch (e) {
        console.error('doctor link state', e);
        setDoctorDisplayLabel(user?.doctor_id ? 'Назначен' : 'Врач не назначен');
      } finally {
        setDoctorLinkLoading(false);
      }
    };
    loadDoctorLinkState();
  }, [user?.id, user?.role, user?.doctor_id]);

  useEffect(() => {
    const loadDoctorQr = async () => {
      if (user?.role !== 'doctor' || !doctorPublicId) {
        setDoctorQrInfo(null);
        return;
      }
      try {
        const res = await doctorLinkAPI.getDoctorQr();
        setDoctorQrInfo(res.data as any);
      } catch {
        setDoctorQrInfo(null);
      }
    };
    loadDoctorQr();
  }, [user, doctorPublicId]);

  useEffect(() => {
    if (user) {
      const nameParts = splitFullName(user.full_name);
      form.setFieldsValue({
        email: user.email,
        phone: normalizePhone(user.phone || '') || user.phone,
        last_name: nameParts.lastName,
        first_name: nameParts.firstName,
        middle_name: nameParts.middleName,
        week_of_pregnancy: user.week_of_pregnancy,
        due_date: user.due_date ? dayjs(user.due_date) : null,
        telegram_username: user.telegram_username || '',
        specialization: user.specialization || '',
        license_number: user.license_number || '',
        doctor: doctorDisplayLabel || 'Врач не назначен',
      });
      // Always set avatar preview from user.avatar_url if it exists
      if (user.avatar_url) {
        console.log('Setting avatar preview from user.avatar_url:', user.avatar_url);
        setAvatarPreview(getFilePreviewUrl(user.avatar_url));
      } else {
        console.log('No avatar_url in user, clearing preview');
        setAvatarPreview(null);
      }
    }
  }, [user, form, doctorDisplayLabel]);

  const refreshSessionUser = async () => {
    try {
      const profileResponse = await authAPI.getProfile();
      const profileData = profileResponse.data as any;
      if (profileData) {
        login({
          user: profileData,
          access_token: localStorage.getItem('access_token') || '',
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  const runDoctorSearch = async () => {
    try {
      setDoctorLinkLoading(true);
      const res = await doctorLinkAPI.searchDoctors({
        clinic_id: doctorSearchClinic,
        q: doctorSearchQ || undefined,
      });
      setDoctorSearchResults((res.data as any[]) ?? []);
    } catch (e: any) {
      message.error(e.response?.data?.detail || 'Ошибка поиска');
    } finally {
      setDoctorLinkLoading(false);
    }
  };

  const handleRequestDoctorLink = async (values: any) => {
    try {
      setDoctorLinkLoading(true);
      await doctorLinkAPI.requestDoctorLink({
        doctor_public_id: values.doctor_public_id.trim(),
        access_level: values.access_level,
      });
      message.success('Заявка отправлена врачу');
      linkForm.resetFields();
      const r = await doctorLinkAPI.getDoctorLinkState();
      const bundle = (r.data as any)?.bundle || null;
      setDoctorLinkBundle(bundle);
      setDoctorDisplayLabel(labelFromDoctorBundle(bundle));
      await refreshSessionUser();
    } catch (e: any) {
      message.error(e.response?.data?.detail || 'Не удалось отправить заявку');
    } finally {
      setDoctorLinkLoading(false);
    }
  };

  const handlePatchDoctorLink = async (values: any) => {
    try {
      setDoctorLinkLoading(true);
      await doctorLinkAPI.patchDoctorLink({
        access_level: values.access_level,
        is_data_hidden_temporarily: values.is_data_hidden_temporarily,
      });
      message.success('Настройки обмена обновлены');
      const r = await doctorLinkAPI.getDoctorLinkState();
      const bundle = (r.data as any)?.bundle || null;
      setDoctorLinkBundle(bundle);
      setDoctorDisplayLabel(labelFromDoctorBundle(bundle));
    } catch (e: any) {
      message.error(e.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setDoctorLinkLoading(false);
    }
  };

  const handleRevokeDoctorLink = async () => {
    try {
      setDoctorLinkLoading(true);
      await doctorLinkAPI.revokeDoctorLink();
      message.success('Привязка снята или заявка отменена');
      setDoctorLinkBundle(null);
      setDoctorDisplayLabel('Врач не назначен');
      await refreshSessionUser();
    } catch (e: any) {
      message.error(e.response?.data?.detail || 'Ошибка отвязки');
    } finally {
      setDoctorLinkLoading(false);
    }
  };

  const handleAvatarUpload = async () => {
    if (!avatarFile) {
      message.warning('Выберите файл для загрузки');
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      if (!appendUploadToFormData(formData, 'avatar', avatarFile, 'avatar.jpg')) {
        message.error('Не удалось прочитать выбранный файл');
        return;
      }

      const avatarResponse = await authAPI.updateProfilePartial(formData);
      const updatedUser = avatarResponse.data as any;

      if (updatedUser?.avatar_url) {
        login({
          user: updatedUser,
          access_token: localStorage.getItem('access_token'),
        });
        setAvatarPreview(getFilePreviewUrl(updatedUser.avatar_url));
        setAvatarFile(null);
        message.success('Аватар успешно загружен!');
      } else {
        message.error('Ошибка: URL аватара не получен');
      }
    } catch (avatarError: any) {
      console.error('Error uploading avatar:', avatarError);
      message.error(avatarError.response?.data?.detail || 'Ошибка загрузки аватара');
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values: any) => {
    try {
      setLoading(true);

      if (avatarFile) {
        try {
          const formData = new FormData();
          if (appendUploadToFormData(formData, 'avatar', avatarFile, 'avatar.jpg')) {
            const avatarResponse = await authAPI.updateProfilePartial(formData);
            const avatarData = avatarResponse.data as any;
            if (avatarData?.avatar_url) {
              try {
                const profileResponse = await authAPI.getProfile();
                const profileData = profileResponse.data as any;
                if (profileData) {
                  login({
                    user: profileData,
                    access_token: localStorage.getItem('access_token'),
                  });
                  setAvatarPreview(getFilePreviewUrl(profileData.avatar_url));
                } else {
                  login({
                    user: avatarData,
                    access_token: localStorage.getItem('access_token'),
                  });
                  setAvatarPreview(getFilePreviewUrl(avatarData.avatar_url));
                }
              } catch {
                login({
                  user: avatarData,
                  access_token: localStorage.getItem('access_token'),
                });
                setAvatarPreview(getFilePreviewUrl(avatarData.avatar_url));
              }
              setAvatarFile(null);
            }
          }
        } catch (avatarError: any) {
          console.error('Error uploading avatar:', avatarError);
          message.error(avatarError.response?.data?.detail || 'Ошибка загрузки аватара');
        }
      }
      
      // Update other fields via PUT (only if there are other fields to update)
      // Skip PUT if only avatar was uploaded (to avoid overwriting avatar_url)
      const includePregnancyFields = user?.role !== 'doctor';
      const joinedFullName = joinFullName({
        lastName: values.last_name,
        firstName: values.first_name,
        middleName: values.middle_name,
      });
      if (!joinedFullName) {
        message.error('Укажите фамилию и имя');
        setLoading(false);
        return;
      }
      const hasOtherFieldsToUpdate = values.phone !== user?.phone || 
                                     joinedFullName !== user?.full_name ||
                                     (includePregnancyFields && (
                                       values.week_of_pregnancy !== user?.week_of_pregnancy ||
                                       (values.due_date ? values.due_date.format('YYYY-MM-DD') : null) !== user?.due_date
                                     )) ||
                                     values.telegram_username !== (user?.telegram_username || '') ||
                                     (user?.role === 'doctor' && (
                                       values.specialization !== (user?.specialization || '') ||
                                       values.license_number !== (user?.license_number || '')
                                     ));
      
      if (hasOtherFieldsToUpdate) {
        const updateData: any = {
          phone: values.phone,
          full_name: joinedFullName,
        };

        if (includePregnancyFields) {
          updateData.week_of_pregnancy = values.week_of_pregnancy;
          updateData.due_date = values.due_date ? values.due_date.format('YYYY-MM-DD') : null;
        }
        
        if (values.telegram_username) {
          updateData.telegram_username = values.telegram_username;
        }
        if (user?.role === 'doctor') {
          if (values.specialization) updateData.specialization = values.specialization;
          if (values.license_number) updateData.license_number = values.license_number;
        }
        
        console.log('Sending PUT request for other fields...');
        console.log('Current user avatar_url before PUT:', user?.avatar_url);
        
        const response = await authAPI.updateProfile(updateData);
        const updatedUser = response.data as any;
        console.log('PUT response received, avatar_url:', updatedUser?.avatar_url);
        
        // CRITICAL: Always reload user from API after PUT to get latest avatar_url
        // This ensures we have the correct avatar_url from database
        try {
          console.log('Reloading profile from API after PUT...');
          const profileResponse = await authAPI.getProfile();
          const profileData = profileResponse.data as any;
          console.log('GET /me response:', profileData);
          console.log('GET /me avatar_url:', profileData?.avatar_url);
          
          if (profileData) {
            // Use the fresh data from GET /me - it has the latest avatar_url
            Object.assign(updatedUser, profileData);
            console.log('Updated user from GET /me, avatar_url:', updatedUser?.avatar_url);
          }
        } catch (reloadError) {
          console.error('Failed to reload profile after PUT:', reloadError);
          // Fallback: preserve avatar_url from before PUT
          if (user?.avatar_url) {
            updatedUser.avatar_url = user.avatar_url;
            console.log('Fallback: Preserved avatar_url from before PUT:', updatedUser.avatar_url);
          }
        }
        
        // Update the auth context with the new user data
        login({ 
          user: updatedUser, 
          access_token: localStorage.getItem('access_token') 
        });
        
        // Update avatar preview if it exists in updated user
        if (updatedUser.avatar_url) {
          console.log('Setting avatar preview to:', updatedUser.avatar_url);
          setAvatarPreview(getFilePreviewUrl(updatedUser.avatar_url));
        } else {
          console.warn('No avatar_url in updatedUser after PUT and reload!');
        }
      }
      
      message.success('Профиль успешно обновлен!');
      setEditing(false);
      // Don't clear avatarFile - it's needed for preview
      // Only clear if we successfully uploaded and got URL back
      if (avatarFile) {
        setAvatarFile(null);
      }
    } catch (error: any) {
      console.error('Error updating profile:', error);
      message.error(error.response?.data?.detail || 'Ошибка обновления профиля. Пожалуйста, попробуйте снова.');
    } finally {
      setLoading(false);
    }
  };

  const toggleEdit = () => {
    if (editing) {
      // Cancel edit and reset form
      if (user) {
        const nameParts = splitFullName(user.full_name);
        form.setFieldsValue({
          email: user.email,
          phone: normalizePhone(user.phone || '') || user.phone,
          last_name: nameParts.lastName,
          first_name: nameParts.firstName,
          middle_name: nameParts.middleName,
          week_of_pregnancy: user.week_of_pregnancy,
          due_date: user.due_date ? dayjs(user.due_date) : null,
          telegram_username: user.telegram_username || '',
          specialization: user.specialization || '',
          license_number: user.license_number || '',
        });
        setAvatarPreview(user.avatar_url ? getFilePreviewUrl(user.avatar_url) : null);
      }
      setEditing(false);
      setAvatarFile(null);
    } else {
      setEditing(true);
    }
  };

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <div style={{ padding: isMobile ? '12px' : '20px' }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ color: '#263238' }}>Профиль</Title>
        <Typography.Text type="secondary">
          {user?.role === 'doctor'
            ? 'Управление личной и профессиональной информацией врача'
            : 'Управление личной информацией и данными беременности'}
        </Typography.Text>
      </div>

      <Card style={{ borderRadius: '12px' }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          disabled={!editing}
        >
          <Row gutter={24}>
            <Col span={24} md={12}>
              <Form.Item
                label="Email"
                name="email"
              >
                <Input 
                  prefix={<MailOutlined />} 
                  placeholder="your.email@example.com"
                  size="large"
                  disabled
                />
              </Form.Item>
            </Col>
            
            <Col span={24} md={12}>
              <Form.Item
                label="Телефон"
                name="phone"
                rules={[
                  { required: true, message: PHONE_REQUIRED_MESSAGE },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const count = getPhoneDigitCount(value);
                      if (count !== 10) return Promise.reject(new Error(PHONE_VALIDATION_MESSAGE));
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <PhoneInput placeholder="912 345 67 89" size="large" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={24} md={8}>
              <Form.Item
                label="Фамилия"
                name="last_name"
                rules={[{ required: true, message: 'Пожалуйста, введите фамилию' }]}
              >
                <Input prefix={<UserOutlined />} placeholder="Иванова" size="large" />
              </Form.Item>
            </Col>
            <Col span={24} md={8}>
              <Form.Item
                label="Имя"
                name="first_name"
                rules={[{ required: true, message: 'Пожалуйста, введите имя' }]}
              >
                <Input placeholder="Анна" size="large" />
              </Form.Item>
            </Col>
            <Col span={24} md={8}>
              <Form.Item label="Отчество (если есть)" name="middle_name">
                <Input placeholder="Сергеевна" size="large" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            {user?.role !== 'doctor' && (
              <Col span={24} md={12}>
                <Form.Item
                  label="Срок беременности (неделя)"
                  name="week_of_pregnancy"
                  rules={[
                    { required: true, message: 'Пожалуйста, укажите срок беременности' },
                    { type: 'number', min: 1, max: 42, message: 'Срок должен быть от 1 до 42 недель' }
                  ]}
                  extra="При изменении недели ПДР пересчитается автоматически"
                >
                  <InputNumber 
                    placeholder="Неделя"
                    min={1}
                    max={42}
                    style={{ width: '100%' }}
                    size="large"
                    onChange={(week) => {
                      if (week == null || pregnancySyncLockRef.current === 'due') return;
                      pregnancySyncLockRef.current = 'week';
                      form.setFieldValue('due_date', calculateDueDateFromWeek(Number(week)));
                      pregnancySyncLockRef.current = null;
                    }}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>

          <Row gutter={24}>
            {user?.role !== 'doctor' && (
              <Col span={24} md={12}>
                <Form.Item
                  label="ПДР"
                  name="due_date"
                  rules={[{ required: true, message: 'Пожалуйста, укажите предполагаемую дату родов' }]}
                  extra="При изменении ПДР неделя пересчитается автоматически"
                >
                  <DatePicker 
                    placeholder="Выберите дату"
                    style={{ width: '100%' }}
                    size="large"
                    suffixIcon={<CalendarOutlined />}
                    format="DD.MM.YYYY"
                    onChange={(value: Dayjs | null) => {
                      if (!value || pregnancySyncLockRef.current === 'week') return;
                      pregnancySyncLockRef.current = 'due';
                      form.setFieldValue('week_of_pregnancy', calculateWeekFromDueDate(value));
                      pregnancySyncLockRef.current = null;
                    }}
                  />
                </Form.Item>
              </Col>
            )}
            
            <Col span={24} md={user?.role === 'doctor' ? 24 : 12}>
              <Form.Item
                label="Telegram username"
                name="telegram_username"
              >
                <Input 
                  placeholder="@username"
                  size="large"
                  prefix={<span>@</span>}
                />
              </Form.Item>
            </Col>
          </Row>

          {user?.role === 'doctor' && (
            <Row gutter={24}>
              <Col span={24} md={12}>
                <Form.Item
                  label="Специальность"
                  name="specialization"
                >
                  <Input 
                    placeholder="Акушер-гинеколог"
                    size="large"
                  />
                </Form.Item>
              </Col>
              
              <Col span={24} md={12}>
                <Form.Item
                  label="Номер лицензии"
                  name="license_number"
                >
                  <Input 
                    placeholder="ЛО-XX-XX-XXXXXX"
                    size="large"
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Row gutter={24}>
            <Col span={24} md={12}>
              <Form.Item label="Аватар">
                <Space direction="vertical" size="middle">
                  <Avatar
                    src={avatarPreview || undefined}
                    size={100}
                    icon={<UserOutlined />}
                  />
                  {editing && (
                    <Upload
                      accept="image/*"
                      maxCount={1}
                      showUploadList={false}
                      beforeUpload={(file) => {
                        const raw = getUploadRawFile(file) || file;
                        if (!(raw instanceof Blob)) {
                          message.error('Не удалось выбрать файл');
                          return false;
                        }
                        const asFile =
                          raw instanceof File
                            ? raw
                            : new File([raw], 'avatar.jpg', { type: raw.type || 'image/jpeg' });
                        setAvatarFile(asFile);
                        const reader = new FileReader();
                        reader.onload = (e) => {
                          setAvatarPreview((e.target?.result as string) || null);
                        };
                        reader.readAsDataURL(asFile);
                        return false;
                      }}
                    >
                      <Button icon={<CameraOutlined />}>
                        {avatarPreview || user?.avatar_url ? 'Сменить фото' : 'Загрузить фото'}
                      </Button>
                    </Upload>
                  )}
                  {editing && avatarFile && (
                    <div>
                      <Button
                        type="primary"
                        onClick={handleAvatarUpload}
                        loading={loading}
                        block
                        size="large"
                      >
                        Сохранить аватар
                      </Button>
                      <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
                        Выбран файл: {avatarFile.name} ({(avatarFile.size / 1024).toFixed(1)} KB)
                      </div>
                    </div>
                  )}
                </Space>
              </Form.Item>
            </Col>
            
            {user?.role !== 'doctor' && (
              <Col span={24} md={12}>
                <Form.Item
                  label="Врач"
                  name="doctor"
                >
                  <Input 
                    size="large"
                    disabled
                    placeholder="Врач"
                  />
                </Form.Item>
              </Col>
            )}
          </Row>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: isMobile ? '100%' : 'auto' }}>
              {editing && (
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  size="large"
                  style={{ 
                    backgroundColor: '#7CB342',
                    borderColor: '#7CB342',
                    borderRadius: '8px',
                    width: isMobile ? '100%' : 'auto'
                  }}
                  block={isMobile}
                >
                  Сохранить изменения
                </Button>
              )}
              
              <Button
                type={editing ? 'default' : 'primary'}
                size="large"
                onClick={toggleEdit}
                style={{ 
                  borderRadius: '8px',
                  width: isMobile ? '100%' : 'auto',
                  ...(editing ? {} : {
                    backgroundColor: '#7CB342',
                    borderColor: '#7CB342'
                  })
                }}
                disabled={loading}
                block={isMobile}
              >
                {editing ? 'Отмена' : 'Редактировать профиль'}
              </Button>
              {editing && user?.role === 'patient' && (
                <Button
                  danger
                  size="large"
                  loading={deletingAccount}
                  block={isMobile}
                  onClick={() => {
                    Modal.confirm({
                      title: 'Вы точно хотите удалить учётную запись?',
                      content: 'Все данные будут удалены без возможности восстановления.',
                      okText: 'Удалить',
                      okType: 'danger',
                      cancelText: 'Отмена',
                      onOk: async () => {
                        try {
                          setDeletingAccount(true);
                          await authAPI.deleteAccount();
                          message.success('Учётная запись удалена');
                          logout();
                        } catch (error: any) {
                          message.error(error.response?.data?.detail || 'Не удалось удалить учётную запись');
                          throw error;
                        } finally {
                          setDeletingAccount(false);
                        }
                      },
                    });
                  }}
                >
                  Удалить учётную запись
                </Button>
              )}
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {user?.role === 'doctor' && (
        <Card title="Публичный код и QR для пациентов" style={{ marginTop: 24, borderRadius: '12px' }}>
          {(user as any).doctor_public_id ? (
            <DoctorPublicIdCard
              doctorPublicId={(user as any).doctor_public_id}
              clinicName={doctorQrInfo?.clinic_name}
              clinicCity={doctorQrInfo?.clinic_city}
              deepLink={doctorQrInfo?.deep_link}
            />
          ) : (
            <Alert
              type="warning"
              showIcon
              message="Публичный ID ещё не выдан"
              description="Администратор должен указать клинику в вашем профиле — после сохранения появится код DR-… и QR."
            />
          )}
        </Card>
      )}

      {user?.role === 'patient' && (
        <Card
          title="Привязка к лечащему врачу УЗИ"
          loading={doctorLinkLoading}
          style={{ marginTop: 24, borderRadius: '12px' }}
        >
          {doctorLinkBundle?.link?.status === 'pending' && (
            <Space direction="vertical" style={{ width: '100%' }}>
              <Alert
                type="info"
                message="Заявка на рассмотрении"
                description={`Врач: ${doctorLinkBundle.doctor_full_name || '—'} · ID: ${doctorLinkBundle.doctor_public_id || '—'}`}
              />
              <Button danger onClick={handleRevokeDoctorLink} loading={doctorLinkLoading}>
                Отменить заявку
              </Button>
            </Space>
          )}

          {doctorLinkBundle?.link?.status === 'active' && (
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Typography.Text strong>
                {doctorLinkBundle.doctor_full_name} · {doctorLinkBundle.doctor_public_id}
              </Typography.Text>
              <Typography.Text type="secondary">
                {doctorLinkBundle.clinic_name}
                {doctorLinkBundle.clinic_city ? `, ${doctorLinkBundle.clinic_city}` : ''}
              </Typography.Text>
              <Form
                layout="vertical"
                initialValues={{
                  access_level: doctorLinkBundle.link.access_level,
                  is_data_hidden_temporarily: doctorLinkBundle.link.is_data_hidden_temporarily,
                }}
                onFinish={handlePatchDoctorLink}
                key={doctorLinkBundle.link.id}
              >
                <Form.Item label="Уровень доступа к дневнику" name="access_level">
                  <Select
                    options={[
                      { value: 'ultrasound_only', label: 'Только замеры УЗИ и животика' },
                      { value: 'ultrasound_labs', label: 'УЗИ и лабораторные анализы' },
                      { value: 'full', label: 'Весь дневник' },
                    ]}
                  />
                </Form.Item>
                <Form.Item
                  label="Временно скрыть данные от врача"
                  name="is_data_hidden_temporarily"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
                <Button type="primary" htmlType="submit">
                  Сохранить настройки обмена
                </Button>
              </Form>
              <Divider />
              <Button danger onClick={handleRevokeDoctorLink} loading={doctorLinkLoading}>
                Отвязаться от врача
              </Button>
            </Space>
          )}

          {!doctorLinkBundle ||
            (doctorLinkBundle?.link?.status !== 'pending' &&
              doctorLinkBundle?.link?.status !== 'active') ? (
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
                Укажите публичный код врача вида DR-КЛИНИКА-XXXXXX или найдите врача по клинике и фамилии.
              </Typography.Paragraph>
              <Row gutter={16}>
                <Col xs={24} md={10}>
                  <Select
                    allowClear
                    placeholder="Клиника (для поиска)"
                    style={{ width: '100%' }}
                    options={clinicsOptions}
                    value={doctorSearchClinic}
                    onChange={(v) => setDoctorSearchClinic(v)}
                  />
                </Col>
                <Col xs={24} md={10}>
                  <Input
                    placeholder="Фрагмент ФИО врача"
                    value={doctorSearchQ}
                    onChange={(e) => setDoctorSearchQ(e.target.value)}
                  />
                </Col>
                <Col xs={24} md={4}>
                  <Button onClick={runDoctorSearch} loading={doctorLinkLoading} block>
                    Найти
                  </Button>
                </Col>
              </Row>
              {doctorSearchResults.length > 0 && (
                <Card size="small" title="Результаты поиска">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {doctorSearchResults.map((d: any) => (
                      <Button
                        key={d.doctor_public_id}
                        type="dashed"
                        block
                        onClick={() =>
                          linkForm.setFieldsValue({ doctor_public_id: d.doctor_public_id })
                        }
                      >
                        {d.display_name} · {d.doctor_public_id}
                        <br />
                        <Typography.Text type="secondary">
                          {d.clinic_name}
                          {d.clinic_city ? `, ${d.clinic_city}` : ''}
                        </Typography.Text>
                      </Button>
                    ))}
                  </Space>
                </Card>
              )}
              <Input.TextArea
                placeholder='Вставьте JSON из QR или код DR-КЛИНИКА-XXXXXX'
                value={qrPaste}
                onChange={(e) => setQrPaste(e.target.value)}
                rows={2}
                style={{ marginBottom: 8 }}
              />
              <Button
                type="dashed"
                block
                style={{ marginBottom: 16 }}
                onClick={() => {
                  const parsed = parseDoctorIdFromQrText(qrPaste);
                  if (parsed) {
                    linkForm.setFieldsValue({ doctor_public_id: parsed });
                    message.success('Код врача подставлен из QR');
                  } else {
                    message.warning('Не удалось распознать код. Ожидается DR-… или JSON из QR.');
                  }
                }}
              >
                Распознать из вставленного QR / кода
              </Button>
              <Form form={linkForm} layout="vertical" onFinish={handleRequestDoctorLink}>
                <Form.Item
                  label="Публичный ID врача"
                  name="doctor_public_id"
                  rules={[{ required: true, message: 'Укажите код врача' }]}
                >
                  <Input placeholder="DR-SPB01-A3F9K2" />
                </Form.Item>
                <Form.Item
                  label="Желаемый доступ"
                  name="access_level"
                  initialValue="full"
                  rules={[{ required: true }]}
                >
                  <Select
                    options={[
                      { value: 'ultrasound_only', label: 'Только УЗИ / животик' },
                      { value: 'ultrasound_labs', label: 'УЗИ и анализы' },
                      { value: 'full', label: 'Весь дневник' },
                    ]}
                  />
                </Form.Item>
                <Button type="primary" htmlType="submit" loading={doctorLinkLoading}>
                  Отправить запрос врачу
                </Button>
              </Form>
            </Space>
          ) : null}
        </Card>
      )}

      {user?.role !== 'doctor' && (
        <>
          {/* Pregnancy Summary Card */}
          <Card 
            title="Информация о беременности" 
            style={{ marginTop: 24, borderRadius: '12px' }}
          >
            <Row gutter={24}>
              <Col span={24} md={6}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ 
                    fontSize: '24px', 
                    fontWeight: 'bold', 
                    color: '#7CB342',
                    marginBottom: 8
                  }}>
                    {user ? getCurrentWeekOfPregnancy(user) || 0 : 0}
                  </div>
                  <div style={{ color: '#5A7B8C' }}>неделя</div>
                </div>
              </Col>
              
              <Col span={24} md={6}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ 
                    fontSize: '18px', 
                    fontWeight: 'bold', 
                    color: '#7CB342',
                    marginBottom: 8
                  }}>
                    {user?.due_date ? format(new Date(user.due_date), 'dd.MM.yyyy') : 'Не указана'}
                  </div>
                  <div style={{ color: '#5A7B8C' }}>ПДР</div>
                </div>
              </Col>
              
              <Col span={24} md={6}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ 
                    fontSize: '18px', 
                    fontWeight: 'bold', 
                    color: '#263238',
                    marginBottom: 8
                  }}>
                    {doctorDisplayLabel || 'Врач не назначен'}
                  </div>
                  <div style={{ color: '#5A7B8C' }}>Врач</div>
                </div>
              </Col>
              
              <Col span={24} md={6}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ 
                    fontSize: '18px', 
                    fontWeight: 'bold', 
                    color: '#7CB342',
                    marginBottom: 8
                  }}>
                    {user?.is_active ? 'Активен' : 'Неактивен'}
                  </div>
                  <div style={{ color: '#5A7B8C' }}>Статус</div>
                </div>
              </Col>
            </Row>
          </Card>

          {/* Health Tips */}
          <Card 
            title="Советы по уходу за собой" 
            style={{ marginTop: 24, borderRadius: '12px' }}
          >
            <Row gutter={16}>
              <Col span={24} md={12}>
                <Card size="small" style={{ height: '100%', borderRadius: '8px' }}>
                  <Typography.Text strong>Физическая активность</Typography.Text>
                  <Typography.Paragraph>
                    Регулярные прогулки, легкая гимнастика и плавание помогают поддерживать тонус 
                    и подготовить организм к родам.
                  </Typography.Paragraph>
                </Card>
              </Col>
              <Col span={24} md={12}>
                <Card size="small" style={{ height: '100%', borderRadius: '8px' }}>
                  <Typography.Text strong>Психологическое состояние</Typography.Text>
                  <Typography.Paragraph>
                    Позвольте себе отдых, практикуйте дыхательные упражнения, 
                    общайтесь с другими будущими мамами.
                  </Typography.Paragraph>
                </Card>
              </Col>
            </Row>
          </Card>
        </>
      )}
    </div>
  );
};